/**
 * MOCK · ASSUMED (CT-5718) — a record's own Sandbox: what people said, grouped by
 * source, waiting to be confirmed — and the conflicts between them.
 *
 * The seed is the prototype's (record PER-0104): the renewal call with three
 * claims (one in conflict with Priya Nair's comment), the migration-timeline
 * emails, two sets of call notes, the interaction history and what Concierge
 * inferred — plus the QBR transcript and the access-review log Drive lists.
 * Every group from a file points at it (`source_ref`), and Drive counts each
 * file's claims from here (`countMockSandboxClaimsByFile`), so "View claims"
 * lands on exactly that many; a group that IS a file shares its date
 * (`KNOWLEDGE_FILE_DATES`).
 *
 * Reads the Knowledge QA switch (`?mockKnowledge=empty|fail|action_fail`).
 */

import { getMockEntityHeaders } from './entityHeader.mock.js';
import { getMockKnowledgeScenario, KNOWLEDGE_FILE_DATES } from './knowledgeSeed.mock.js';

/** Per record: claim statuses changed by the user, claims sent, conflicts resolved. */
let state = new Map();

const recordKey = (entityType, entityId) => `${entityType}/${entityId}`;
const recordState = (entityType, entityId) => {
  const key = recordKey(entityType, entityId);
  if (!state.has(key)) state.set(key, { status: {}, sent: new Set(), resolved: new Set() });
  return state.get(key);
};

const nameOf = (entityType, entityId) => {
  const rows = getMockEntityHeaders()[entityType] ?? [];
  const row = rows.find((candidate) => (candidate.entity_id ?? candidate.entityId ?? candidate.id) === entityId);
  return row?.title ?? row?.name ?? 'this contact';
};

const claim = (fields) => ({ status: 'to_confirm', can_confirm: true, sent: false, ...fields });

const buildGroups = (name) => [
  {
    id: 'grp-renewal-call',
    label: 'Call · Renewal follow-up',
    occurred_at: KNOWLEDGE_FILE_DATES['file-call-transcript'],
    source_type: 'said',
    channel: 'call',
    source_ref: { kind: 'file', id: 'file-call-transcript', folder_id: 'root' },
    claims: [
      claim({ id: 'cl-reports-to', label: 'Reports to', value: 'Daniel Reyes, COO', category: 'identity_role', quote: 'I report to Daniel Reyes, our COO. He signs off anything over budget.', speaker: name, file: 'Call-transcript-2026-09-02.txt', timestamp: '1:05' }),
      claim({ id: 'cl-funding', label: 'Evaluation funding', value: 'Funded for this cycle', category: 'budget_dates', quote: 'The evaluation is still funded for this cycle. That part is settled.', speaker: name, file: 'Call-transcript-2026-09-02.txt', timestamp: '2:10' }),
      claim({ id: 'cl-pricing', label: 'Pricing', value: 'Not an objection', category: 'budget_dates', quote: 'Pricing is fine on our side. It is the timeline we need.', speaker: name, file: 'Call-transcript-2026-09-02.txt', timestamp: '4:35', status: 'conflict' }),
    ],
  },
  {
    id: 'grp-timeline-email',
    label: 'Email · Migration timeline',
    occurred_at: '2026-08-18T15:00:00Z',
    period_label: 'Aug 12 – 18, 2026',
    source_type: 'documented',
    channel: 'email',
    source_ref: { kind: 'file', id: 'file-migration', folder_id: 'root' },
    claims: [
      claim({ id: 'cl-timeline-written', label: 'Wants', value: 'The migration timeline in writing', category: 'needs', quote: 'Could you share the migration timeline in writing once it is confirmed?', speaker: name, file: 'Email · Aug 12', timestamp: null }),
      claim({ id: 'cl-residency', label: 'Requirement', value: 'Data-residency note with the timeline', category: 'needs', quote: 'Please include the data-residency note with it.', speaker: name, file: 'Email · Aug 18', timestamp: null }),
    ],
  },
  {
    id: 'grp-notes-aug28',
    label: 'Call notes — Aug 28',
    occurred_at: '2026-08-28T15:00:00Z',
    source_type: 'said',
    channel: 'notes',
    source_ref: null,
    claims: [claim({ id: 'cl-formal-name', label: 'Name in proposals', value: name, category: 'identity_role', quote: `${name} asked us to keep the full name in anything formal.`, speaker: 'Priya Nair', file: 'Call notes · Aug 28', timestamp: null })],
  },
  {
    id: 'grp-notes-jun12',
    label: 'Call notes — Jun 12',
    occurred_at: '2026-06-12T15:00:00Z',
    source_type: 'said',
    channel: 'notes',
    source_ref: null,
    claims: [claim({ id: 'cl-escalations', label: 'Open escalations', value: 'Two, no resolution date', category: 'risk', quote: 'The two July escalations still have no resolution date.', speaker: 'Priya Nair', file: 'Call notes · Jun 12', timestamp: null })],
  },
  {
    id: 'grp-qbr',
    label: 'QBR · Escalations',
    occurred_at: KNOWLEDGE_FILE_DATES['file-qbr'],
    source_type: 'said',
    channel: 'call',
    source_ref: { kind: 'file', id: 'file-qbr', folder_id: 'root' },
    claims: [
      claim({ id: 'cl-qbr-escalations', label: 'Escalations', value: 'Two still open', category: 'risk', quote: 'We still have the two escalations from July without a resolution date.', speaker: name, file: 'QBR-transcript-aug26.txt', timestamp: '12:40' }),
      claim({ id: 'cl-qbr-owner', label: 'Escalation owner', value: 'Daniel Reyes', category: 'identity_role', quote: 'Daniel owns the escalations on our side.', speaker: name, file: 'QBR-transcript-aug26.txt', timestamp: '14:05' }),
      claim({ id: 'cl-qbr-deadline', label: 'Wants', value: 'A resolution date before renewal', category: 'needs', quote: 'We need a date for both before we talk renewal.', speaker: name, file: 'QBR-transcript-aug26.txt', timestamp: '15:30' }),
    ],
  },
  {
    id: 'grp-access-log',
    label: 'Email · Evidence pack delivered',
    occurred_at: KNOWLEDGE_FILE_DATES['file-access-log'],
    source_type: 'documented',
    channel: 'email',
    source_ref: { kind: 'file', id: 'file-access-log', folder_id: 'root' },
    claims: [
      claim({ id: 'cl-access-review', label: 'Access review', value: 'Completed for Q3', category: 'risk', quote: 'The Q3 access review is complete; the log is attached.', speaker: 'Priya Nair', file: 'Access-review-log-Q3.pdf', timestamp: null }),
    ],
  },
  {
    id: 'grp-history',
    label: 'Interaction history',
    occurred_at: '2026-08-28T15:00:00Z',
    source_type: 'observed',
    channel: 'history',
    source_ref: null,
    claims: [claim({ id: 'cl-reply-time', label: 'Reply time', value: 'About two days', category: 'communication', quote: null, speaker: 'Concierge', file: 'Interaction history', timestamp: null })],
  },
  {
    id: 'grp-inferred',
    label: 'Inferred by Concierge',
    occurred_at: null,
    period_label: "From the record's history",
    source_type: 'observed',
    channel: 'inferred',
    source_ref: null,
    claims: [
      claim({ id: 'cl-decision-role', label: 'Decision role', value: 'Technical evaluator', category: 'identity_role', quote: null, speaker: 'Concierge', file: 'Inferred by Concierge', timestamp: null, status: 'inferred' }),
      claim({ id: 'cl-signing', label: 'Signing authority', value: 'Above $250K: Daniel Reyes', category: 'budget_dates', quote: null, speaker: 'Concierge', file: 'Inferred by Concierge', timestamp: null, status: 'inferred' }),
      claim({ id: 'cl-criterion', label: 'Deciding criterion', value: 'A written timeline', category: 'needs', quote: null, speaker: 'Concierge', file: 'Inferred by Concierge', timestamp: null, status: 'inferred' }),
    ],
  },
];

/** How many claims each file holds (Drive's "N claims extracted"), whatever their status. */
export const countMockSandboxClaimsByFile = (name) =>
  buildGroups(name).reduce((counts, group) => {
    const fileId = group.source_ref?.kind === 'file' ? group.source_ref.id : null;
    if (fileId) counts[fileId] = (counts[fileId] ?? 0) + group.claims.length;
    return counts;
  }, {});

/** What each one said about a claim in conflict. */
const buildConflicts = (name) => ({
  'cl-pricing': {
    label: 'Pricing',
    statements: [
      { id: 'cl-pricing', person: name, source_type: 'said', source: 'Sep 2 call · 4:35', quote: 'Pricing is fine on our side. It is the timeline we need.', value: 'Not an objection' },
      { id: 'cmt-priya-pricing', person: 'Priya Nair', source_type: null, source: 'Sep 3 · comment on the call note', quote: 'Settled for this cycle only. The renewal uplift has not been put to them yet.', value: null },
    ],
  },
});

const QUICK_FILTERS = {
  all: () => true,
  to_confirm: (row) => row.status === 'to_confirm',
  conflicts: (row) => row.status === 'conflict',
  inferred: (row) => row.status === 'inferred',
};

/** Newest first; a group with no date (what Concierge inferred) always last. */
const byDate = (direction) => (a, b) => {
  if (!a.occurred_at || !b.occurred_at) return (a.occurred_at ? 0 : 1) - (b.occurred_at ? 0 : 1);
  const diff = new Date(a.occurred_at) - new Date(b.occurred_at);
  return direction === 'asc' ? diff : -diff;
};

/** Most recent (`occurred_at`), Most claims (`claim_count`), Conflicts first (`conflicts`); ties newest first. */
const sign = (direction) => (direction === 'asc' ? 1 : -1);
const conflictRank = (group) => (group.state === 'conflict' ? 1 : 0);
const newestFirst = byDate('desc');

const ORDERS = {
  occurred_at: (direction) => byDate(direction),
  claim_count: (direction) => (a, b) => sign(direction) * (a.claim_count - b.claim_count) || newestFirst(a, b),
  conflicts: (direction) => (a, b) => sign(direction) * (conflictRank(a) - conflictRank(b)) || newestFirst(a, b),
};

const matchesSearch = (row, search = {}) => {
  const needle = (search.query ?? '').trim().toLowerCase();
  if (!needle) return true;
  const fields = search.fields?.length ? search.fields : ['label', 'value'];
  return fields.some((field) => String(row[field] ?? '').toLowerCase().includes(needle));
};

const withRecordState = (record) => (group) => {
  const claims = group.claims.map((row) => ({
    ...row,
    status: record.status[row.id] ?? row.status,
    sent: record.sent.has(row.id),
  }));
  return { ...group, claims, state: claims.some((row) => row.status === 'conflict') ? 'conflict' : null };
};

export const listMockSandboxGroups = ({
  entityType,
  entityId,
  orderBy = 'occurred_at',
  orderDirection = 'desc',
  quickFilter = 'all',
  filters = {},
  search,
}) => {
  const scenario = getMockKnowledgeScenario();
  if (scenario === 'fail') return { error: { status: 500, description: 'Mock Sandbox failure (?mockKnowledge=fail).' } };
  if (scenario === 'empty') return { body: { groups: [] }, meta: {} };

  const record = recordState(entityType, entityId);
  const seed = buildGroups(nameOf(entityType, entityId));
  const sources = filters.source ?? [];
  const types = filters.source_type ?? [];
  const groups = seed
    .map(withRecordState(record))
    .filter((group) => !sources.length || sources.includes(group.source_ref?.id ?? group.id))
    .filter((group) => !types.length || types.includes(group.source_type))
    .map((group) => {
      const claims = group.claims
        .filter(QUICK_FILTERS[quickFilter] ?? QUICK_FILTERS.all)
        .filter((row) => matchesSearch(row, search));
      return { ...group, claims, claim_count: claims.length };
    })
    .filter((group) => group.claims.length > 0)
    .sort((ORDERS[orderBy] ?? ORDERS.occurred_at)(orderDirection));

  // The "Source" options of All filters: every source this record's claims came from.
  const sourceOptions = seed.map((group) => ({ value: group.source_ref?.id ?? group.id, label: group.label }));

  return { body: { groups, source_options: sourceOptions }, meta: { total_items: groups.length } };
};

const findClaim = (entityType, entityId, claimId) =>
  buildGroups(nameOf(entityType, entityId))
    .flatMap((group) => group.claims)
    .find((row) => row.id === claimId);

const mutate = (entityType, entityId, claimId, apply) => {
  if (getMockKnowledgeScenario() === 'action_fail') {
    return { error: { status: 500, description: 'Mock claim failure (?mockKnowledge=action_fail).' } };
  }
  if (!findClaim(entityType, entityId, claimId)) return { error: { status: 404, description: `No claim ${claimId}` } };
  apply(recordState(entityType, entityId));
  return { body: { claim_id: claimId }, meta: {} };
};

export const confirmMockClaim = ({ entityType, entityId, claimId }) =>
  mutate(entityType, entityId, claimId, (record) => {
    record.status[claimId] = 'confirmed';
  });

export const sendMockClaimForConfirmation = ({ entityType, entityId, claimId }) =>
  mutate(entityType, entityId, claimId, (record) => {
    record.sent.add(claimId);
  });

export const getMockConflict = ({ entityType, entityId, claimId }) => {
  const conflict = buildConflicts(nameOf(entityType, entityId))[claimId];
  if (!conflict) return { error: { status: 404, description: `No conflict on ${claimId}` } };
  const record = recordState(entityType, entityId);
  // Whoever may confirm the claim may resolve its conflict.
  const canConfirm = findClaim(entityType, entityId, claimId)?.can_confirm ?? false;
  return { body: { claim_id: claimId, ...conflict, resolved: record.resolved.has(claimId), can_confirm: canConfirm }, meta: {} };
};

/** `choice` is a statement id, or `unsure` to send it for confirmation. */
export const resolveMockConflict = ({ entityType, entityId, claimId, choice }) =>
  mutate(entityType, entityId, claimId, (record) => {
    record.resolved.add(claimId);
    if (choice === 'unsure') {
      record.status[claimId] = 'to_confirm';
      record.sent.add(claimId);
    } else {
      record.status[claimId] = 'confirmed';
    }
  });

export const __resetMockSandboxForTests = () => {
  state = new Map();
};
